<template lang="pug">
v-container.px-xs-0.pb-16(:style="containerStyles")
  FormTopSection(
    @save="saveForm"
    @cancel="onCompleted"
    :formTitle="formTitle"
    :disable-save-button="disableSaveButton"
    :default-message="defaultAlertMessage"
    :form-error="formError"
    :field-errors="fieldErrors")
  v-form(ref="form", v-model="valid")
    FormExpansionPanel(
      ref="expansionPanel",
      :hasDangerZone="isEdit",
      :allowed-to-publish="allowedToPublish",
      @validate="validateOnChange"
    )
  v-row.mt-4.mt-sm-8.pa-sm-4(
    align-content="stretch",
    align="stretch",
    justify="end"
  )
    v-col.d-flex.pa-sm-0.pr-sm-4(cols="12", lg="7", sm="6")
      FormAlert(
        :field-errors="fieldErrors"
        :error="formError"
        :tipp="alertTipp"
      )
    v-col.pa-sm-0(cols="12", lg="5", sm="6")
      FormActions(
        @save="saveForm"
        @cancel="onCompleted"
        :disable-save-button="disableSaveButton"
      )
</template>

<script lang="ts">
import Header from "../UI/Header.vue"
import Button from "../UI/Button.vue"
import TextButton from "../UI/TextButton.vue"
import TextBody from "../UI/TextBody.vue"
import ScrollTo from "../UI/ScrollTo.vue"
import FormExpansionPanel from "features/events/form/FormExpansionPanel.vue"
import { createNamespacedHelpers } from "vuex"
import FormTopSection from "features/common/forms/FormTopSection.vue"
import FormActions from "features/common/forms/FormActions.vue"
import FormAlertTipp, { Tipp } from "features/common/forms/FormAlertTipp.vue"
import FormAlert, { FieldError } from "features/common/forms/FormAlert.vue"
import { defineComponent, provide } from "vue"
import { useCanSendUrgentOnLevelOrClientFallback } from "features/common/permissions/usePermissionCheckHelper"
import { PanelIdentifier } from "features/events/form/event-form.types"
import EventFormService from "features/events/form/event-form.service"
import store from "store"
import { Event, AttachmentUpload, Group } from "suus-api"
import { GroupOrMember } from "components/EventForm/OrganizerFields.vue"
import { Rule } from "features/common/forms/form.service"
import { GroupAccess } from "components/TopicForm/general-fields/group-access"
import { useOwnUserAttendanceContext } from "app/modules/events/use-own-user-attendance-context.concern"

const { mapState, mapActions, mapMutations } = createNamespacedHelpers("event")

export type EventForm = {
  title: string
  description: string | null
  location: string
  start_date: string
  start_time: string | null
  end_date: string | null
  end_time: string | null
  all_day: boolean
  urgent: boolean
  online: boolean
  reminders: any[]
  allow_guests: boolean
  nonmember_access: GroupAccess
  group: Group | undefined
  contact: GroupOrMember
  contact_type: string | undefined
  custom_call_url: string | null
  is_public: boolean
  attachments: AttachmentUpload[]
}

type EventFormDTO = {
  title: string
  description: string | null
  location: string
  start_date: string
  start_time: string | null
  end_date: string | null
  end_time: string | null
  all_day: boolean
  urgent: boolean
  online: boolean
  reminders: any[]
  allow_guests: boolean
  nonmember_access: GroupAccess
  group_id: number | undefined
  contact_id: number | undefined
  contact_type: string | undefined
  custom_call_url: string | null
  is_public: boolean
}

export default defineComponent({
  name: "EventForm",
  components: {
    FormAlert,
    FormAlertTipp,
    FormActions,
    FormTopSection,
    Header,
    Button,
    TextButton,
    ScrollTo,
    FormExpansionPanel,
    TextBody,
  },
  props: {
    isEdit: { type: Boolean, default: false },
    scrollTo: { type: String, default: "" },
  },
  watch: {
    scrollTo: {
      handler(newVal) {
        if (newVal !== "") this.scrollIntoView(newVal)
      },
      immediate: true,
    },
  },
  data: () => ({
    isSaving: false,
    isSaved: false,
    valid: true,
    showSheet: false,
    fieldErrors: [] as FieldError[],
  }),
  computed: {
    disableSaveButton(): boolean {
      return this.isSaving
    },
    defaultAlertMessage(): string {
      return this.$t("components.event_form.default_info_message").toString()
    },
    allowedToPublish(): boolean {
      return (
        !!(
          this.currentEvent?.data &&
          this.currentEvent?.data?.links &&
          (this.currentEvent?.data?.links.publish_url ||
            this.currentEvent?.data?.links.unpublish_url)
        ) ||
        useCanSendUrgentOnLevelOrClientFallback(this.eventForm?.group?.level_id)
          .value
      )
    },
    formTitle(): string {
      return this.isEdit
        ? this.$t("components.event_form.edit_event").toString()
        : this.$t("components.event_form.new_event").toString()
    },
    containerStyles() {
      return { maxWidth: "770px" }
    },
    alertTipp(): Tipp {
      const description =
        this.eventFormService.getFormValue("description").value
      if (description && description.length) {
        return {
          label: this.$t(
            "components.event_form.info_notifications_label"
          ).toString(),
          selector: "suus__event-form__notifications",
          message: this.$t(
            "components.event_form.info_notifications_message"
          ).toString(),
          actionBeforeCall: () => {
            this.addActivePanel(PanelIdentifier.REMINDER)
          },
        }
      }
      return {
        label: this.$t(
          "components.event_form.info_description_label"
        ).toString(),
        selector: "suus__event-form__description",
        message: this.$t(
          "components.event_form.info_description_message"
        ).toString(),
        actionBeforeCall: () => {
          this.addActivePanel(PanelIdentifier.GENERAL)
        },
      }
    },
    ...mapState(["eventForm", "currentEvent"]),
  },
  methods: {
    convertFormToDTO(eventForm: EventForm): EventFormDTO {
      return {
        title: eventForm.title,
        description: eventForm.description || null,
        location: eventForm.location,
        start_date: eventForm.start_date,
        start_time: eventForm.start_time || null,
        end_date: eventForm.end_date || null,
        end_time: eventForm.end_time || "23:59:59",
        all_day: eventForm.all_day,
        urgent: eventForm.urgent,
        online: eventForm.online,
        reminders: eventForm.reminders,
        allow_guests: eventForm.allow_guests,
        nonmember_access: eventForm.nonmember_access,
        group_id: eventForm.group && eventForm.group.id,
        contact_id: eventForm.contact && eventForm.contact.id,
        contact_type:
          eventForm?.contact_type ||
          (!!eventForm.contact && "type" in eventForm.contact
            ? eventForm?.contact?.type
            : null),
        custom_call_url: eventForm.custom_call_url || null,
        is_public: eventForm.is_public,
      }
    },
    addActivePanel(panelId: PanelIdentifier) {
      ;(
        this.$refs.expansionPanel as InstanceType<typeof FormExpansionPanel>
      ).addActivePanel(panelId)
    },
    onCompleted() {
      this.$emit("completed", true)
      this.isSaving = false
    },
    async saveForm() {
      this.isSaving = true
      this.isSaved = true
      const isEditMode = !!this.currentEvent?.data?.id
      this.eventFormService.setFormError(null)
      if (this.eventFormService.validate()) {
        if (isEditMode) {
          await this.editEvent()
        } else {
          await this.createNewEvent(
            this.convertFormToDTO(this.eventFormService.getFormValues())
          )
        }
      }
      this.isSaving = false
    },
    async createNewEvent(requestBody: EventFormDTO) {
      try {
        const event: Event = await this.createEvent(requestBody)
        this.onCompleted()
        window.open(`/events/${event.id}`, "_self")
      } catch (e: any) {
        console.log(e)
        this.eventFormService.setFormError(e instanceof Error ? e.message : e)
      }
    },
    async editEvent() {
      try {
        await this.updateEvent({
          event_id: this.currentEvent.data.id,
          request_body: this.convertFormToDTO(
            this.eventFormService.getFormValues()
          ),
        })
        this.checkAttendanceStatus()
        this.onCompleted()
      } catch (e: any) {
        console.log(e)
        this.eventFormService.setFormError(e instanceof Error ? e.message : e)
      }
    },
    scrollIntoView(selector) {
      //const element = document.querySelector(selector)
      setTimeout(() => {
        const element = document.getElementById(selector)
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      }, 500)
    },
    validationWatcher(failedRules: Rule<EventForm>[]) {
      if (!this.isSaved) {
        return true
      }
      this.fieldErrors = failedRules.map((rule: Rule<EventForm>) => {
        this.addActivePanel(rule.panelIdentifier)
        return {
          message: rule.message,
          selector: rule.selector,
        }
      })
      return !this.fieldErrors.length
    },
    validateOnChange() {
      if (this.isSaved) {
        this.eventFormService.validate()
      }
    },
    ...mapActions(["createEvent", "updateEvent", "addPublic", "deletePublic"]),
    ...mapMutations(["SET_EVENT_ERROR", "SET_OPTIMISTIC_ATTACHMENTS_COUNT"]),
  },
  mounted() {
    this.eventFormService.registerValidationWatcher(this.validationWatcher)
  },
  setup(props) {
    const eventFormService = new EventFormService<EventForm>(store)
    provide<EventFormService<EventForm>>("EventFormService", eventFormService)
    const formError = eventFormService.formError

    let checkAttendanceStatus = () => {
      //pass
    }
    if (props.isEdit) {
      const { checkAttendanceStatus: checkAttendanceStatusFromContext } =
        useOwnUserAttendanceContext()
      checkAttendanceStatus = checkAttendanceStatusFromContext
    }

    return { eventFormService, formError, checkAttendanceStatus }
  },
})
</script>
